import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, ToggleButtonGroup, ToggleButton, Skeleton } from '@mui/material';
import { DashboardService } from '../service';
import { useCurrency } from '../../../utils/currency';

export default function PerformanceChart({ initialData }) {
    const { currencySymbol } = useCurrency();
    const [period, setPeriod] = useState('Week');
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hoveredIdx, setHoveredIdx] = useState(null);

    const getEmptyBaseline = () => [
        { day: 'Mon', occ: 0, rev: 0 },
        { day: 'Tue', occ: 0, rev: 0 },
        { day: 'Wed', occ: 0, rev: 0 },
        { day: 'Thu', occ: 0, rev: 0 },
        { day: 'Fri', occ: 0, rev: 0 },
        { day: 'Sat', occ: 0, rev: 0 },
        { day: 'Sun', occ: 0, rev: 0 }
    ];

    const fetchChartData = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const apiPeriod = period === 'Week' ? 'this_week' : 'this_month';
            const res = await DashboardService.fetchWeeklyChart(tenantId, { period: apiPeriod });
            const dataArray = Array.isArray(res) ? res : (res && Array.isArray(res.chartData) ? res.chartData : (res && Array.isArray(res.data) ? res.data : null));
            if (dataArray && dataArray.length > 0) {
                setChartData(dataArray);
            } else {
                setChartData(getEmptyBaseline());
            }
        } catch (err) {
            console.error('Failed to fetch performance chart data:', err);
            setChartData(getEmptyBaseline());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (period === 'Week' && initialData && initialData.length > 0) {
            setChartData(initialData);
        } else {
            fetchChartData();
        }
    }, [period, initialData]);

    const data = chartData && chartData.length > 0 ? chartData : getEmptyBaseline();

    const maxRev = Math.max(...data.map(d => {
        return Number(d.revenue !== undefined ? d.revenue : (d.rev !== undefined ? d.rev : 0)) || 0;
    }), 10000);

    const N = data.length;
    
    // SVG Dimensions
    const svgWidth = 650;
    const svgHeight = 240;
    const paddingLeft = 45;
    const paddingRight = 45;
    const paddingTop = 25;
    const paddingBottom = 35;
    
    const plotWidth = svgWidth - paddingLeft - paddingRight;
    const plotHeight = svgHeight - paddingTop - paddingBottom;

    const getOccY = (occValue) => {
        const val = Math.min(100, Math.max(0, occValue));
        return paddingTop + plotHeight - (val / 100) * plotHeight;
    };

    const getRevY = (revValue) => {
        return paddingTop + plotHeight - (revValue / maxRev) * plotHeight;
    };

    const gridLevels = [0, 25, 50, 75, 100];

    const points = data.map((item, idx) => {
        let dayLabel = item.day || '';
        if (!dayLabel && item.date) {
            try {
                const d = new Date(item.date);
                if (!isNaN(d.getTime())) {
                    dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' });
                } else {
                    dayLabel = item.date;
                }
            } catch (e) {
                dayLabel = item.date;
            }
        }
        
        const occVal = item.occRate !== undefined ? item.occRate : (item.occupancy !== undefined ? item.occupancy : (item.occ !== undefined ? item.occ : 0));
        const revVal = item.revenue !== undefined ? item.revenue : (item.rev !== undefined ? item.rev : 0);
        
        const x = N === 1 ? paddingLeft + plotWidth / 2 : paddingLeft + (idx * (plotWidth / (N - 1)));
        const yOcc = getOccY(occVal);
        const yRev = getRevY(revVal);
        
        return {
            x,
            yOcc,
            yRev,
            label: dayLabel,
            occVal,
            revVal
        };
    });

    const occLinePath = N > 1 ? points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.yOcc}`).join(' ') : '';
    const revLinePath = N > 1 ? points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.yRev}`).join(' ') : '';
    const revAreaPath = N > 1 ? `${revLinePath} L ${points[N - 1].x} ${paddingTop + plotHeight} L ${points[0].x} ${paddingTop + plotHeight} Z` : '';

    return (
        <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
            border: '1px solid #f1f5f9',
            height: '100%' 
        }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                {/* Header: Title, Subtitle, Legend & Toggle */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', md: 'center' }, 
                    gap: 1.5, 
                    mb: 2 
                }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                            Performance Analytics
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                            Weekly comparison of occupancy and revenue.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        {/* Legend */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#6366f1' }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                                    Revenue
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10b981' }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                                    Occupancy
                                </Typography>
                            </Box>
                        </Box>

                        {/* Week / Month Toggle */}
                        <ToggleButtonGroup
                            value={period}
                            exclusive
                            onChange={(e, v) => v && setPeriod(v)}
                            size="small"
                            sx={{ 
                                height: 28,
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                p: 0.25,
                                '& .MuiToggleButton-root': { 
                                    border: 'none',
                                    borderRadius: '6px !important',
                                    px: 1.25,
                                    py: 0,
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    color: '#64748b',
                                    textTransform: 'none',
                                    '&.Mui-selected': {
                                        bgcolor: '#ffffff',
                                        color: '#0f172a',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="Week">Week</ToggleButton>
                            <ToggleButton value="Month">Month</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {/* SVG Chart */}
                <Box sx={{ width: '100%', position: 'relative', pt: 1 }}>
                    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" height="auto" style={{ overflow: 'visible' }}>
                        <defs>
                            <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.12} />
                                <stop offset="100%" stopColor="#6366f1" stopOpacity={0.0} />
                            </linearGradient>
                        </defs>

                        {/* Dashed Grid Lines & Left/Right Labels */}
                        {gridLevels.map(level => {
                            const y = getOccY(level);
                            const revValue = Math.round((level / 100) * maxRev);
                            const revLabel = revValue >= 1000 ? `${currencySymbol}${Math.round(revValue / 1000)}k` : `${currencySymbol}${revValue}`;

                            return (
                                <g key={level}>
                                    <line 
                                        x1={paddingLeft} 
                                        y1={y} 
                                        x2={paddingLeft + plotWidth} 
                                        y2={y} 
                                        stroke="rgba(0,0,0,0.04)" 
                                        strokeDasharray="4 4" 
                                    />
                                    {/* Left Axis: Occupancy % */}
                                    <text 
                                        x={paddingLeft - 8} 
                                        y={y + 3} 
                                        textAnchor="end" 
                                        fontSize={9} 
                                        fill="#94a3b8"
                                    >
                                        {level}%
                                    </text>
                                    {/* Right Axis: Revenue ₹ */}
                                    <text 
                                        x={paddingLeft + plotWidth + 8} 
                                        y={y + 3} 
                                        textAnchor="start" 
                                        fontSize={9} 
                                        fill="#94a3b8"
                                    >
                                        {revLabel}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Revenue Area Fill */}
                        {N > 1 && (
                            <path 
                                d={revAreaPath} 
                                fill="url(#revGradient)" 
                                style={{ transition: 'all 0.3s ease' }}
                            />
                        )}

                        {/* Revenue Line (Purple #6366f1) */}
                        {N > 1 && (
                            <path 
                                d={revLinePath} 
                                fill="none" 
                                stroke="#6366f1" 
                                strokeWidth="2.2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                style={{ transition: 'all 0.3s ease' }}
                            />
                        )}

                        {/* Occupancy Line (Teal #10b981) */}
                        {N > 1 && (
                            <path 
                                d={occLinePath} 
                                fill="none" 
                                stroke="#10b981" 
                                strokeWidth="2.2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                style={{ transition: 'all 0.3s ease' }}
                            />
                        )}

                        {/* Points & Interactive Hover Columns */}
                        {points.map((p, idx) => {
                            const isHovered = hoveredIdx === idx;
                            const isSat = p.label.toLowerCase().includes('sat');

                            return (
                                <g key={idx}>
                                    {/* Transparent click/hover column */}
                                    <rect 
                                        x={p.x - plotWidth / (N * 2)} 
                                        y={paddingTop} 
                                        width={plotWidth / N} 
                                        height={plotHeight} 
                                        fill="transparent" 
                                        onMouseEnter={() => setHoveredIdx(idx)}
                                        onMouseLeave={() => setHoveredIdx(null)}
                                        style={{ cursor: 'pointer' }}
                                    />

                                    {/* Active/Hover Vertical Line */}
                                    {isHovered && (
                                        <line 
                                            x1={p.x} 
                                            y1={paddingTop} 
                                            x2={p.x} 
                                            y2={paddingTop + plotHeight} 
                                            stroke="rgba(99, 102, 241, 0.2)" 
                                            strokeDasharray="2 2" 
                                        />
                                    )}

                                    {/* Indicator Dots on Saturday or Active Hover */}
                                    {(isSat || isHovered) && (
                                        <>
                                            {/* Revenue Point Dot */}
                                            <circle 
                                                cx={p.x} 
                                                cy={p.yRev} 
                                                r={isHovered ? 4.5 : 3.5} 
                                                fill="#6366f1" 
                                                stroke="#ffffff" 
                                                strokeWidth={1.5} 
                                            />

                                            {/* Occupancy Point Dot */}
                                            <circle 
                                                cx={p.x} 
                                                cy={p.yOcc} 
                                                r={isHovered ? 4.5 : 3.5} 
                                                fill="#10b981" 
                                                stroke="#ffffff" 
                                                strokeWidth={1.5} 
                                            />
                                        </>
                                    )}

                                    {/* X-axis Day Label */}
                                    <text 
                                        x={p.x} 
                                        y={paddingTop + plotHeight + 18} 
                                        textAnchor="middle" 
                                        fontSize={10} 
                                        fill={isSat || isHovered ? '#0f172a' : '#94a3b8'} 
                                        fontWeight={isSat || isHovered ? 700 : 500}
                                    >
                                        {p.label}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>

                    {/* Hover Floating Tooltip */}
                    {hoveredIdx !== null && points[hoveredIdx] && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${(points[hoveredIdx].x / svgWidth) * 100}%`,
                            top: 20,
                            transform: 'translateX(-50%)',
                            bgcolor: '#0f172a',
                            color: '#ffffff',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            pointerEvents: 'none',
                            fontSize: '0.72rem',
                            zIndex: 10,
                            whiteSpace: 'nowrap'
                        }}>
                            <Box sx={{ fontWeight: 700, mb: 0.25 }}>{points[hoveredIdx].label}</Box>
                            <Box sx={{ color: '#a5b4fc' }}>Rev: {currencySymbol}{points[hoveredIdx].revVal.toLocaleString()}</Box>
                            <Box sx={{ color: '#6ee7b7' }}>Occ: {points[hoveredIdx].occVal}%</Box>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
